import * as React from 'react';
import { CustomerCard } from './AppointmentWizardCustomerCard.tsx/CustomerCard';
import {
  AppointmentStatus,
  AppointmentType,
  CreateAppointmentInput,
  UpdateAppointmentInput,
} from 'src/graphql/__generated__/globalTypes';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/shadcn/components/ui/select';
import { Textarea } from 'src/shadcn/components/ui/textarea';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { DateTime } from 'luxon';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from 'src/shadcn/components/ui/form';
import { ShopLocationPicker } from 'src/Components/ShopLocationPicker/ShopLocationPicker';
import { useAuthContext } from 'src/Components/AuthContext';
import { Button } from 'src/shadcn/components/ui/button';
import { gql } from '@apollo/client';
import {
  AppointmentWizardBodyFragment,
  useCreateAppointmentMutation,
  useUpdateAppointmentMutation,
} from './__generated__/AppointmentWizardBody.generated';
import { useHotToast } from 'src/utils/useHotToast';
import { v4 } from 'uuid';
import { useHandleOnGoingAway } from 'src/utils/dirtyFormNavigationHook';
import { useShopLocationsContext } from 'src/Components/WorkOrdersPage/Contexts/ShopLocationsContext';
import { ShopLocationContextFragment } from 'src/Components/WorkOrdersPage/Contexts/__generated__/ShopLocationsContext.generated';
import { RotateCw } from 'lucide-react';
import { GetDayAppointmentsForWeekDocument } from '../AppointmentsWeekView/__generated__/AppointmentsWeekView.generated';
import { AppointmentDatePicker } from 'src/Components/appt/components/AppointmentDatePicker';
import { ListRequestedAppointmentsDocument } from 'src/Components/Pages/DashboardPage/lib/__generated__/useAppointmentsRequestContext.generated';
import { CustomerCallSoundPlayer } from 'src/Components/CustomerPage/CustomerCallSoundPlayer';

export interface IAppointmentWizardBodyProps {
  selectedAppointment: AppointmentWizardBodyFragment | undefined;
  defaultDropoffTime?: DateTime;
  onClose: () => void;
}

export const AppointmentWizardBody: React.FunctionComponent<
  IAppointmentWizardBodyProps
> = props => {
  const { form } = useAppointmentWizardBodyFrom(props);
  useHandleOnGoingAway({
    isDirty: form.formState.isDirty,
    warningText: 'You have unsaved changes. Are you sure you want to leave?',
  });
  const authContext = useAuthContext();
  const [activeButton, setActiveButton] = React.useState<
    'saveAndClose' | 'approveAndSave' | 'saveWithoutApprove'
  >();
  const toast = useHotToast();
  const { updateAppointment, isUpdatingAppointment } = useUpdateAppointment(
    props.onClose
  );
  const { createNewAppointment, isCreatingAppointment } =
    useCreateNewAppointment(props.onClose);

  const { shopLocations, isLoadingLocations } = useShopLocationsContext();
  return (
    <>
      {props.selectedAppointment?.callReport?.recordingURL && (
        <div className='max-w-96 px-5 py-10 md:px-0'>
          <CustomerCallSoundPlayer
            audioSrc={props.selectedAppointment.callReport.recordingURL}
            title={'Requested through a phone call'}
          />
        </div>
      )}
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(data => {
            const input = {
              expectedDropoffTime: data.expectedDropoffTime,
              estimatedPickupTime: new Date(), // TODO
              customerId: data.customerAndCar.customer?.id,
              carId: data.customerAndCar.car?.id,
              notes: data.notes,
              appointmentType: data.appointmentType,
              status: data.status,
            };
            if (props.selectedAppointment) {
              updateAppointment(
                {
                  id: props.selectedAppointment.id,
                  ...input,
                },
                () => {
                  props.onClose();
                }
              );
            } else {
              const shopLocationId = data.shopLocation?.id;
              if (shopLocationId) {
                createNewAppointment(
                  {
                    ...input,
                    id: v4(),
                  },
                  shopLocationId,
                  data
                );
              } else {
                toast.error('Please select a location');
              }
            }
          })}
          className='w-full max-w-96 flex-1 space-y-6'
        >
          <FormField
            control={form.control}
            name='expectedDropoffTime'
            render={({ field }) => (
              <FormItem className='flex flex-col'>
                <FormLabel>Drop off time</FormLabel>
                <AppointmentDatePicker
                  fieldValue={field.value}
                  setFieldValue={newDropoffTime => {
                    field.onChange(newDropoffTime);
                  }}
                />
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name='status'
            render={({ field }) => (
              <FormItem>
                <FormLabel>Appointment status</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value ? String(field.value) : undefined}
                >
                  <FormControl>
                    <SelectTrigger className='flex'>
                      <SelectValue placeholder='Select appointment Status' />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectGroup>
                      {[
                        AppointmentStatus.Requested,
                        AppointmentStatus.Active,
                        AppointmentStatus.Completed,
                        AppointmentStatus.Canceled,
                        AppointmentStatus.NoShow,
                        AppointmentStatus.InProgress,
                      ].map(appointmentStatus => {
                        return (
                          <SelectItem
                            key={appointmentStatus}
                            value={appointmentStatus}
                          >
                            {appointmentStatusToDisplayText(appointmentStatus)}
                          </SelectItem>
                        );
                      })}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name='appointmentType'
            render={({ field }) => (
              <FormItem>
                <FormLabel>Appointment type</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value ? String(field.value) : undefined}
                >
                  <FormControl>
                    <SelectTrigger className='flex'>
                      <SelectValue placeholder='Select appointment type' />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectGroup>
                      {[
                        AppointmentType.dropoff,
                        AppointmentType.waitAtShop,
                      ].map(appointmentType => {
                        return (
                          <SelectItem
                            key={appointmentType}
                            value={appointmentType}
                          >
                            {appointmentTypeToDisplayText(appointmentType) ||
                              ''}
                          </SelectItem>
                        );
                      })}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name='customerAndCar'
            render={({ field }) => (
              <FormItem>
                <FormLabel>Customer & Car</FormLabel>
                <FormControl>
                  <CustomerCard
                    customer={field.value.customer || undefined}
                    car={field.value.car || undefined}
                    setCustomer={customer => {
                      if (customer?.id === field.value.customer?.id) {
                        return;
                      }
                      if (!customer) {
                        field.onChange({
                          ...field.value,
                          customer,
                          car: undefined,
                        });
                      } else {
                        field.onChange({ ...field.value, customer });
                      }
                    }}
                    setCar={car => {
                      field.onChange({ ...field.value, car });
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name='notes'
            render={({ field }) => (
              <FormItem>
                <FormLabel>Notes</FormLabel>
                <FormControl>
                  <Textarea placeholder='Appointment notes' {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name='shopLocation'
            render={({ field }) => {
              return (
                <FormItem
                  hidden={
                    Boolean(authContext.user?.shopLocation?.id) ||
                    (shopLocations.length === 1 && !isLoadingLocations)
                  }
                >
                  <FormLabel>Shop location</FormLabel>
                  <FormControl>
                    <ShopLocationPicker
                      selectedLocation={field.value}
                      onLocationPicked={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
          <Button type='submit' disabled={!form.formState.isDirty}>
            <span>Save & close</span>
            {activeButton === 'saveAndClose' &&
              (isCreatingAppointment || isUpdatingAppointment) && (
                <RotateCw className='ml-2 h-4 w-4 animate-spin' />
              )}
          </Button>
        </form>
      </Form>
    </>
  );
};

function appointmentTypeToDisplayText(
  type: AppointmentType | undefined
): string | undefined {
  switch (type) {
    case AppointmentType.dropoff:
      return 'Drop off';
    case AppointmentType.waitAtShop:
      return 'Wait at shop';
    case undefined:
      return undefined;
  }
}

function appointmentStatusToDisplayText(status: AppointmentStatus | undefined) {
  switch (status) {
    case AppointmentStatus.Requested:
      return 'Requested';
    case AppointmentStatus.Active:
      return 'Confirmed';
    case AppointmentStatus.Completed:
      return 'Completed';
    case AppointmentStatus.Canceled:
      return 'Canceled';
    case AppointmentStatus.NoShow:
      return 'No show';
    case AppointmentStatus.InProgress:
      return 'In progress';
    case undefined:
      return undefined;
  }
}

function useAppointmentWizardBodyFrom(props: IAppointmentWizardBodyProps) {
  const shopLocation = useGetDefaultShopLocation(props);
  const defaultValues = {
    expectedDropoffTime: getDefaultExpectedDropoffDate(props),
    appointmentType:
      props.selectedAppointment?.appointmentType || AppointmentType.dropoff,
    customerAndCar: {
      customer: props.selectedAppointment?.customer || undefined,
      car: props.selectedAppointment?.car || undefined,
    },
    notes: props.selectedAppointment?.notes || undefined,
    shopLocation,
    status: props.selectedAppointment?.status || AppointmentStatus.Active,
  };
  const form = useForm({
    resolver: zodResolver(
      z.object({
        expectedDropoffTime: z.date(),
        appointmentType: z.nativeEnum(AppointmentType),
        status: z.nativeEnum(AppointmentStatus),
        shopLocation: z.object({
          id: z.string(),
          name: z.string(),
        }),
        customerAndCar: z.object({
          customer: z
            .object({
              id: z.string(),
              firstName: z.string().nullish(),
              lastName: z.string().nullish(),
              phoneNumber: z.string().nullish(),
              email: z.string().nullish(),
              secondaryPhoneNumber: z.string().nullish(),
              companyName: z.string().nullish(),
              createdAt: z.any().nullish(),
            })
            .nullish(),
          car: z
            .object({
              id: z.string(),
              make: z.string().nullish(),
              model: z.string().nullish(),
              year: z.number().nullish(),
              colour: z.string().nullish(),
              vin: z.string().nullish(),
              licensePlate: z.string().nullish(),
              createdAt: z.any().nullish(),
            })
            .nullish(),
        }),
        notes: z.string().nullish(),
      })
    ),
    defaultValues,
  });
  return { form };
}

function useGetDefaultShopLocation(
  props: IAppointmentWizardBodyProps
): ShopLocationContextFragment | undefined {
  if (props.selectedAppointment?.shopLocation) {
    return props.selectedAppointment.shopLocation;
  }
  const { shopLocations } = useShopLocationsContext();
  const authContext = useAuthContext();
  if (authContext.user?.shopLocation?.id) {
    return { id: authContext.user?.shopLocation?.id, name: '' };
  }
  if (shopLocations.length === 1) {
    return shopLocations[0];
  }
  return undefined;
}

function getDefaultExpectedDropoffDate(props: IAppointmentWizardBodyProps) {
  if (props.selectedAppointment?.expectedDropoffTime) {
    return new Date(props.selectedAppointment?.expectedDropoffTime);
  } else if (props.defaultDropoffTime) {
    return props.defaultDropoffTime.toJSDate();
  } else {
    return DateTime.now().startOf('day').plus({ day: 1, hours: 9 }).toJSDate();
  }
}

const CREATE_NEW_APPOINTMENT_QUERY = gql`
  mutation CreateAppointment(
    $shopLocationId: String!
    $input: CreateAppointmentInput!
  ) {
    createAppointment(shopLocationId: $shopLocationId, input: $input) {
      id
      expectedDropoffTime
      estimatedPickupTime
      notes
      appointmentType
      status
      customer {
        id
        firstName
        lastName
        phoneNumber
        email
      }
      car {
        createdAt
        licensePlate
        vin
        id
        make
        model
        year
        colour
      }
    }
  }
`;

function useCreateNewAppointment(onClose: () => void) {
  const toast = useHotToast();
  const [createNewAppointment, { loading: isCreatingAppointment }] =
    useCreateAppointmentMutation({
      onError: e => {
        toast.error(e.message);
      },
      refetchQueries: [GetDayAppointmentsForWeekDocument],
    });
  return {
    createNewAppointment: (
      input: CreateAppointmentInput,
      shopLocationId: string,
      data: Parameters<
        Parameters<
          ReturnType<
            typeof useAppointmentWizardBodyFrom
          >['form']['handleSubmit']
        >[0]
      >[0]
    ) => {
      onClose();
      createNewAppointment({
        variables: { input, shopLocationId },
        optimisticResponse: {
          createAppointment: {
            id: input.id,
            expectedDropoffTime: input.expectedDropoffTime,
            estimatedPickupTime: input.estimatedPickupTime,
            customer: input.customerId
              ? {
                  id: input.customerId,
                  firstName: data.customerAndCar.customer?.firstName || null,
                  lastName: data.customerAndCar.customer?.lastName || null,
                  phoneNumber:
                    data.customerAndCar.customer?.phoneNumber || null,
                  email: data.customerAndCar.customer?.email || null,
                }
              : null,
            car: input.carId
              ? {
                  id: input.carId,
                  make: data.customerAndCar.car?.make || null,
                  model: data.customerAndCar.car?.model || null,
                  year: data.customerAndCar.car?.year || 2022,
                  colour: data.customerAndCar.car?.colour || null,
                  vin: data.customerAndCar.car?.vin || null,
                  licensePlate: data.customerAndCar.car?.licensePlate || null,
                  createdAt: data.customerAndCar.car?.createdAt || null,
                }
              : null,
            appointmentType: null,
            notes: input.notes || null,
            status: AppointmentStatus.Active,
          },
        },
        // update: (cache, newAppointmentData) => {
        //   cache.modify({
        //     fields: {
        //       listUpcomingAppointments: updateField(newAppointmentData),
        //       listPastAppointments: updateField(newAppointmentData),
        //     },
        //   });
        // },
      });
    },
    isCreatingAppointment,
  };
}

function useUpdateAppointment(onClose: () => void) {
  const toast = useHotToast();
  const [updateAppointment, { loading: isUpdatingAppointment }] =
    useUpdateAppointmentMutation({
      onError: e => {
        toast.error(e.message);
      },
      refetchQueries: [
        GetDayAppointmentsForWeekDocument,
        ListRequestedAppointmentsDocument,
      ],
    });
  return {
    updateAppointment: (
      input: UpdateAppointmentInput,
      onUpdateComplete: () => void
    ) => {
      updateAppointment({
        variables: { input },
        onCompleted: () => {
          onUpdateComplete();
        },
      });
    },
    isUpdatingAppointment,
  };
}

const UPDATE_APPOINTMENT_QUERY = gql`
  mutation UpdateAppointment($input: UpdateAppointmentInput!) {
    updateAppointment(input: $input) {
      id
      expectedDropoffTime
      estimatedPickupTime
      notes
      appointmentType
      status
      customer {
        id
        firstName
        lastName
        phoneNumber
        email
      }
      car {
        id
        make
        model
        year
        colour
      }
    }
  }
`;

const fragment = gql`
  fragment AppointmentWizardBody on Appointment {
    id
    expectedDropoffTime
    estimatedPickupTime
    requestedOn
    requestedFrom
    notes
    appointmentType
    status
    shopLocation {
      id
      name
    }
    customer {
      id
      firstName
      lastName
      phoneNumber
      email
      secondaryPhoneNumber
      companyName
      createdAt
    }
    car {
      createdAt
      licensePlate
      vin
      id
      make
      model
      year
      colour
    }
    job {
      jobMetadata {
        id
        jobNumber
        mechanicShopStatus
      }
    }
    callReport {
      id
      createdAt
      endReason
      recordingURL
      summary
    }
  }
`;
